import React, { useMemo, useCallback } from "react";
import {
  formatDate,
  Flex,
  Button,
  Space,
  Box,
  pluralize,
  TableCellText,
  TableCell,
} from "@thenounproject/lingo-core";

import InsightsIntervalPicker from "../InsightsIntervalPicker";
import DataTable from "../../table/DataTable";
import { TableColumn } from "../../table";
import useKitPortalPerformance, { KitPortalResult } from "../queries/useKitPortalPerformance";
import useGenerateInsightsCSV from "../hooks/useGenerateInsightsCSV";
import { useInsightsContext } from "@contexts/InsightsProvider";

type Props = {
  kitId: string;
  space: Space;
};

const InsightsKitPortalsTab = ({ kitId, space }: Props) => {
  const [sort, setSort] = React.useState<string>("-total_views");
  const [generateInsightsCSV] = useGenerateInsightsCSV();

  const { dateOperators } = useInsightsContext();

  const {
    data,
    isLoading: tableIsLoading,
    fetchNextPage,
    error: tableError,
  } = useKitPortalPerformance({
    spaceId: space.id,
    sort,
    dateOperators,
    kitId,
  });

  const { portals: tableData, total } = data || {};

  const hasMorePages = tableData?.length < total;

  const downloadCSV = useCallback(async () => {
    await generateInsightsCSV({
      spaceId: space.id,
      id: "portals",
      sort,
      dateOperators,
      additionalParams: { ["kit_uuid"]: kitId },
    });
  }, [dateOperators, generateInsightsCSV, kitId, sort, space.id]);

  const tableHeaderProps = useMemo(() => {
    return {
      title: "Portal Performance",
      subtitle: "Breakdown of how the kit was accessed across different portals.",
      headerExtra: (
        <Flex>
          <InsightsIntervalPicker id={"user-table"} />
          <Button
            icon="action.download"
            buttonStyle="secondary"
            ml="s"
            onClick={downloadCSV}
            disabled={tableData?.length === 0}
          />
        </Flex>
      ),
    };
  }, [downloadCSV, tableData?.length]);

  const tableColumns = useMemo(() => {
    const cols: TableColumn<KitPortalResult>[] = [
      {
        sortKey: "name",
        name: "Portal Name",
        minWidth: 300,
        flexGrow: 1,
        renderer: i => {
          return <TableCell space={space} data={i} />;
        },
      },
      {
        sortKey: "total_views",
        name: "Total Views",
        minWidth: 160,
        justify: "flex-end",
        renderer: i => (
          <Flex alignItems="center" flexWrap="wrap">
            <TableCellText font="ui.regular">
              {i.views.totalViews} {pluralize("view", i.views.totalViews)}
            </TableCellText>
            <TableCellText font="ui.small" color="grayDarker" ml="xs">
              {`/ ${i.views.uniquePeople} ${pluralize("user", i.views.uniquePeople)}`}
            </TableCellText>
          </Flex>
        ),
      },
      {
        sortKey: "member_views",
        name: "User Views",
        minWidth: 160,
        justify: "flex-end",
        renderer: i => (
          <Flex alignItems="center" flexWrap="wrap">
            <TableCellText font="ui.regular">
              {i.views.memberViews} {pluralize("view", i.views.memberViews)}
            </TableCellText>
            <TableCellText font="ui.small" color="grayDarker" ml="xs">
              {`/ ${i.views.uniqueMembers} ${pluralize("user", i.views.uniqueMembers)}`}
            </TableCellText>
          </Flex>
        ),
      },
      {
        sortKey: "guest_views",
        name: "Visitor Views",
        minWidth: 160,
        justify: "flex-end",
        renderer: i => (
          <Flex alignItems="center" flexWrap="wrap">
            <TableCellText font="ui.regular">
              {i.views.guestViews} {pluralize("view", i.views.guestViews)}
            </TableCellText>
            <TableCellText font="ui.small" color="grayDarker" ml="xs">
              {`/ ${i.views.uniqueGuests} ${pluralize("user", i.views.uniqueGuests)}`}
            </TableCellText>
          </Flex>
        ),
      },
      {
        sortKey: "privacy",
        name: "Privacy",
        minWidth: 120,
        renderer: i => (
          <TableCellText>{`${i.privacy.charAt(0).toUpperCase() + i.privacy.slice(1)}`}</TableCellText>
        ),
      },
      {
        name: "Date Added",
        minWidth: 140,
        renderer: i => <TableCellText>{formatDate(i.dateAdded, "MMM d, yyyy")}</TableCellText>,
      },
      {
        name: "Last Updated",
        minWidth: 140,
        renderer: i => (
          <TableCellText>{formatDate(i.views.lastViewed, "MMM d, yyyy")}</TableCellText>
        ),
      },
    ];
    return cols;
  }, [space]);

  return (
    <Box mt="l">
      <DataTable<KitPortalResult>
        data={tableData}
        columns={tableColumns}
        reportTitleProps={tableHeaderProps}
        isLoading={tableIsLoading}
        hasMorePages={hasMorePages}
        fetchNextPage={fetchNextPage}
        onSort={setSort}
        sort={sort}
        tableError={tableError}
      />
    </Box>
  );
};

export default InsightsKitPortalsTab;
